import { useEffect, useState } from "react";
import { DeviceMobile, X } from "@phosphor-icons/react";
import { useTranslation } from "@/contexts/LanguageContext";

const DISMISS_KEY = "pwa-install-dismissed-at";
const DISMISS_COOLDOWN_MS = 14 * 24 * 60 * 60 * 1000; // 14 days

// Detect iOS Safari (which doesn't fire `beforeinstallprompt`).
function isIOS() {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
}

function isInStandalone() {
  // PWA is already installed / launched from home screen
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
}

export default function InstallPrompt() {
  const { lang } = useTranslation();
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [visible, setVisible] = useState(false);
  const [showIosGuide, setShowIosGuide] = useState(false);

  useEffect(() => {
    if (isInStandalone()) return;

    const dismissedAt = parseInt(localStorage.getItem(DISMISS_KEY) || "0", 10);
    if (dismissedAt && Date.now() - dismissedAt < DISMISS_COOLDOWN_MS) return;

    // iOS Safari: we can't programmatically prompt, so show a "how to" banner
    // after 3 seconds on first visit.
    if (isIOS()) {
      const t = setTimeout(() => setVisible(true), 3000);
      return () => clearTimeout(t);
    }

    // Android / Chrome / Edge: capture the install event
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setVisible(true);
    };
    window.addEventListener("beforeinstallprompt", handler);

    const installedHandler = () => {
      setVisible(false);
      setDeferredPrompt(null);
    };
    window.addEventListener("appinstalled", installedHandler);

    return () => {
      window.removeEventListener("beforeinstallprompt", handler);
      window.removeEventListener("appinstalled", installedHandler);
    };
  }, []);

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setVisible(false);
    setShowIosGuide(false);
  };

  const handleInstall = async () => {
    if (isIOS()) {
      setShowIosGuide(true);
      return;
    }
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome !== "accepted") {
      // Re-arm for a later visit
      localStorage.setItem(DISMISS_KEY, String(Date.now()));
    }
    setDeferredPrompt(null);
    setVisible(false);
  };

  if (!visible) return null;

  const copy =
    lang === "es"
      ? {
          title: "Instala DSLR Advisor",
          body: "Anade la app a tu pantalla de inicio para acceso rapido, pantalla completa y sin barras del navegador.",
          install: "Instalar",
          later: "Ahora no",
          ios_title: "Instalar en iPhone / iPad",
          ios_step1: "Toca el boton Compartir en Safari.",
          ios_step2: "Desplazate y toca \"Anadir a pantalla de inicio\".",
          ios_step3: "Confirma con \"Anadir\" en la esquina superior.",
          got_it: "Entendido",
        }
      : {
          title: "Install DSLR Advisor",
          body: "Add the app to your home screen for fast access — fullscreen, no browser bars.",
          install: "Install",
          later: "Not now",
          ios_title: "Install on iPhone / iPad",
          ios_step1: "Tap the Share button in Safari.",
          ios_step2: "Scroll and tap \"Add to Home Screen\".",
          ios_step3: "Confirm with \"Add\" in the top-right.",
          got_it: "Got it",
        };

  if (showIosGuide) {
    return (
      <div
        className="fixed inset-x-0 bottom-0 z-[9998] p-4 sm:p-6 pointer-events-none"
        data-testid="pwa-ios-guide"
      >
        <div className="max-w-md mx-auto bg-[#121214] border border-[#F5A524]/30 rounded-2xl shadow-2xl p-5 pointer-events-auto backdrop-blur-md">
          <div className="flex items-start justify-between gap-3 mb-3">
            <h3 className="font-['Outfit'] text-white text-base font-medium">
              {copy.ios_title}
            </h3>
            <button
              onClick={dismiss}
              className="text-[#71717A] hover:text-white transition-colors p-1 -m-1"
              aria-label={copy.later}
              data-testid="pwa-ios-close"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          <ol className="space-y-2 text-sm text-[#D4D4D8] list-decimal ml-5">
            <li>{copy.ios_step1}</li>
            <li>{copy.ios_step2}</li>
            <li>{copy.ios_step3}</li>
          </ol>
          <button
            onClick={dismiss}
            className="w-full mt-4 bg-[#F5A524] hover:bg-[#F5B041] text-[#0A0A0A] text-sm font-medium rounded-lg py-2 transition-colors"
            data-testid="pwa-ios-confirm"
          >
            {copy.got_it}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      className="fixed inset-x-0 bottom-0 z-[9998] p-4 sm:p-6 pointer-events-none"
      data-testid="pwa-install-banner"
    >
      <div className="max-w-md mx-auto bg-[#121214] border border-[#F5A524]/30 rounded-2xl shadow-2xl p-4 sm:p-5 pointer-events-auto backdrop-blur-md">
        <div className="flex items-start gap-3">
          <div className="shrink-0 w-10 h-10 rounded-xl bg-[#F5A524]/10 border border-[#F5A524]/20 flex items-center justify-center">
            <DeviceMobile weight="fill" className="w-5 h-5 text-[#F5A524]" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-['Outfit'] text-white text-sm sm:text-base font-medium mb-1">
              {copy.title}
            </h3>
            <p className="text-[#A1A1AA] text-xs sm:text-sm leading-relaxed">
              {copy.body}
            </p>
          </div>
          <button
            onClick={dismiss}
            className="text-[#71717A] hover:text-white transition-colors p-1 -m-1"
            aria-label={copy.later}
            data-testid="pwa-install-close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="mt-4 flex items-center justify-end gap-2">
          <button
            onClick={dismiss}
            className="px-3 py-1.5 text-xs text-[#A1A1AA] hover:text-white transition-colors"
            data-testid="pwa-install-later"
          >
            {copy.later}
          </button>
          <button
            onClick={handleInstall}
            className="px-4 py-1.5 bg-[#F5A524] hover:bg-[#F5B041] text-[#0A0A0A] text-xs sm:text-sm font-medium rounded-lg transition-colors"
            data-testid="pwa-install-btn"
          >
            {copy.install}
          </button>
        </div>
      </div>
    </div>
  );
}
